"use client"

import { useState, Fragment, useEffect } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { X, Loader2 } from "lucide-react"
import creditAccountsService from "../../services/creditAccountsService"
import { useCurrentUser } from "../../utils/context/UserProvider"
import toast from "react-hot-toast"

// ─── Icons ────────────────────────────────────────────────────────────────────

const WarningIcon = () => (
  <div className="flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-900/30 mb-4">
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
        stroke="#D97706"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
)

const BulbIcon = () => (
  <div className="flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-900/30 mb-4">
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
      <path
        d="M9 21h6M12 3a6 6 0 016 6c0 2.22-1.2 4.16-3 5.2V17a1 1 0 01-1 1H10a1 1 0 01-1-1v-2.8A6 6 0 0112 3z"
        stroke="#D97706"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
)

const SuccessIcon = () => (
  <div className="flex items-center justify-center w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
      <path
        d="M20 6L9 17l-5-5"
        stroke="#16A34A"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
)

// ─── Modal ────────────────────────────────────────────────────────────────────

const WithdrawalRequestModal = ({ isOpen, onClose, accountInfo, reset }) => {
  const { currentUser } = useCurrentUser()

  // Steps: 1 = Important Notice, 2 = Before You Continue,
  //        3 = Final Confirmation, 4 = Success
  const [step, setStep] = useState(1)

  const [selectedAccount, setSelectedAccount] = useState(null)
  const [loadingAccount, setLoadingAccount] = useState(false)
  const [keepLoading, setKeepLoading] = useState(false)
  const [refundLoading, setRefundLoading] = useState(false)

  // ── Fetch account on open ──────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setStep(1)
      setSelectedAccount(accountInfo || null)
      if (!accountInfo) fetchAccount()
    }
  }, [isOpen])

  const fetchAccount = async () => {
    try {
      setLoadingAccount(true)
      const response = await creditAccountsService.getUserAccount({})
      const data = response?.data
      if (data?.statusCode === 200) {
        const u = data.data
        setSelectedAccount({
          id: u._id,
          name: u.account_name,
          balance: Number(u.balance) || 0,
          currency: u.account_currency || "USD",
        })
      }
    } catch {
      toast.error("Failed to load account info.")
    } finally {
      setLoadingAccount(false)
    }
  }

  const resetModal = () => {
    setStep(1)
    setSelectedAccount(null)
    setKeepLoading(false)
    setRefundLoading(false)
  }

  const handleClose = () => {
    resetModal()
    onClose()
  }

  // ── "Keep My Balance" / "Keep My Account Active" ──────────────────────────
  const handleKeepActive = async () => {
    if (!selectedAccount?.id) {
      handleClose()
      return
    }
    try {
      setKeepLoading(true)
      await creditAccountsService.keepAccountActive({ accountId: selectedAccount.id })
    } catch (err) {
      console.error("keepAccountActive error:", err)
    } finally {
      setKeepLoading(false)
      handleClose()
    }
  }

  // ── "Confirm Refund" ───────────────────────────────────────────────────────
  const handleConfirmRefund = async () => {
    if (!selectedAccount?.id) {
      toast.error("Account not loaded. Please try again.")
      return
    }
    try {
      setRefundLoading(true)
      const response = await creditAccountsService.processWithdraw({
        accountId: selectedAccount.id,
        amount: selectedAccount.balance,
        notes: "Refund request — account termination",
        currency: selectedAccount.currency,
      })
      if (response?.data?.statusCode === 200) {
        setStep(4)
        if (reset) reset()
      } else {
        throw new Error(response?.data?.message || "Refund request failed")
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "An error occurred"
      toast.error(msg)
    } finally {
      setRefundLoading(false)
    }
  }

  const clientId = currentUser?.clientId || currentUser?.reference || "—"

  // ── Step content ───────────────────────────────────────────────────────────
  const renderStep = () => {
    if (loadingAccount && step === 1) {
      return (
        <div className="flex flex-col items-center py-10">
          <Loader2 size={36} className="animate-spin text-neutral-400 mb-3" />
          <p className="text-sm text-neutral-500 dark:text-neutral-400">Loading account…</p>
        </div>
      )
    }

    switch (step) {
      // ── Step 1: Important Notice ────────────────────────────────────────
      case 1:
        return (
          <>
            <div className="flex flex-col items-center text-center px-2">
              <WarningIcon />
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">
                Important Notice
              </h2>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed max-w-xs">
                Requesting a refund will <span className="font-semibold text-neutral-900 dark:text-white">terminate your contract</span> with
                Weberz Corp, and you will no longer be able to continue working with Weberz.
              </p>
            </div>

            <div className="flex gap-3 mt-8">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 py-2.5 rounded-xl border border-neutral-300 dark:border-neutral-700 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setStep(2)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 transition-colors"
              >
                I Understand
              </button>
            </div>
          </>
        )

      // ── Step 2: Before You Continue ─────────────────────────────────────
      case 2:
        return (
          <>
            <div className="flex flex-col items-center text-center px-2">
              <BulbIcon />
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">
                Before You Continue
              </h2>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed max-w-xs">
                Instead of requesting a refund, you may{" "}
                <span className="font-semibold text-neutral-900 dark:text-white">
                  keep your balance
                </span>{" "}
                and use it at any time in the future.
              </p>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed mt-2 max-w-xs">
                Your balance remains{" "}
                <span className="font-semibold text-neutral-900 dark:text-white">
                  safe, secure, and always available
                </span>{" "}
                in your account.
              </p>
            </div>

            <div className="flex gap-3 mt-8">
              <button
                type="button"
                onClick={handleKeepActive}
                disabled={keepLoading}
                className="flex-1 py-2.5 rounded-xl border border-neutral-300 dark:border-neutral-700 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-60"
              >
                {keepLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Processing…
                  </span>
                ) : (
                  "Keep My Balance"
                )}
              </button>
              <button
                type="button"
                onClick={() => setStep(3)}
                disabled={keepLoading}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-60"
              >
                Continue Anyway
              </button>
            </div>
          </>
        )

      // ── Step 3: Final Confirmation ───────────────────────────────────────
      case 3:
        return (
          <>
            <div className="flex flex-col items-center text-center px-2">
              <WarningIcon />
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">
                Final Confirmation
              </h2>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed max-w-xs">
                If you proceed, your{" "}
                <span className="font-semibold text-neutral-900 dark:text-white">
                  client ID ({clientId})
                </span>{" "}
                will be released and reassigned to a new client.
              </p>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed mt-2 max-w-xs">
                Any remaining balance will be refunded, and your access will be closed.
              </p>
            </div>

            <div className="flex gap-3 mt-8">
              <button
                type="button"
                onClick={handleKeepActive}
                disabled={refundLoading || keepLoading}
                className="flex-1 py-2.5 rounded-xl border border-neutral-300 dark:border-neutral-700 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-60"
              >
                {keepLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Processing…
                  </span>
                ) : (
                  "Keep My Account Active"
                )}
              </button>
              <button
                type="button"
                onClick={handleConfirmRefund}
                disabled={refundLoading || keepLoading}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-60"
              >
                {refundLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Submitting…
                  </span>
                ) : (
                  "Confirm Refund"
                )}
              </button>
            </div>
          </>
        )

      // ── Step 4: Success ──────────────────────────────────────────────────
      case 4:
        return (
          <>
            <div className="flex flex-col items-center text-center px-2">
              <SuccessIcon />
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">
                Refund Request Submitted
              </h2>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed max-w-xs">
                Your refund request has been submitted successfully. Our team will review it and proceed accordingly.
              </p>
            </div>

            <div className="mt-8">
              <button
                type="button"
                onClick={handleClose}
                className="w-full py-2.5 rounded-xl text-sm font-semibold text-neutral-900 dark:text-neutral-100 bg-amber-400 hover:bg-amber-500 dark:bg-amber-500 dark:hover:bg-amber-600 transition-colors"
              >
                Close
              </button>
            </div>
          </>
        )

      default:
        return null
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>

        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-sm rounded-2xl bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 shadow-2xl p-7">

                {/* Close button — hidden on step 4 */}
                {step !== 4 && (
                  <button
                    type="button"
                    onClick={handleClose}
                    className="absolute top-4 right-4 p-1.5 rounded-full text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                    aria-label="Close"
                  >
                    <X size={18} />
                  </button>
                )}

                {renderStep()}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>

      </Dialog>
    </Transition>
  )
}

export default WithdrawalRequestModal
